/**
 * Testing API for browser automation and healthchecks
 *
 * Provides window.shiftgearsTest API for:
 * - Sending test messages
 * - Getting message history
 * - Waiting for agent responses
 * - Running healthchecks
 *
 * Usage in browser console:
 *   window.shiftgearsTest.sendMessage('Hello')
 *   window.shiftgearsTest.getMessages()
 *   window.shiftgearsTest.healthcheck()
 *   await window.shiftgearsTest.waitForResponse(10000)
 */

import { browserControlWS } from './browserControlWebSocket';
import { getOrCreateSessionId } from './sessionManager';
import { config } from '../config/runtimeConfig';

/**
 * Internal state - managed by VoiceWidgetConnected
 */
let messagesRef = { current: [] };
let sendMessageFn = null;
let sessionIdRef = { current: null };

/**
 * Set the messages reference (called from VoiceWidgetConnected)
 * @param {Array} messages - Current messages array
 */
export function setMessagesRef(messages) {
  messagesRef.current = messages;
}

/**
 * Set the send message function (called from VoiceWidgetConnected)
 * @param {Function} sendFn - Function to send messages
 */
export function setSendMessageFn(sendFn) {
  sendMessageFn = sendFn;
}

/**
 * Set the session ID (called from VoiceWidgetConnected)
 * @param {string} id - Session ID
 */
export function setSessionId(id) {
  sessionIdRef.current = id;
}

/**
 * Wait for a condition with timeout
 * @param {Function} condition - Function that returns true when condition is met
 * @param {number} timeout - Timeout in milliseconds
 * @param {number} pollInterval - Polling interval in milliseconds
 * @returns {Promise<boolean>} - True if condition was met, false if timeout
 */
async function waitForCondition(condition, timeout = 10000, pollInterval = 100) {
  const startTime = Date.now();
  while (Date.now() - startTime < timeout) {
    if (condition()) {
      return true;
    }
    await new Promise(resolve => setTimeout(resolve, pollInterval));
  }
  return false;
}

/**
 * Testing API object exposed on window
 */
const testingApi = {
  /**
   * Send a text message to the AI agent
   * @param {string} text - Message text
   * @returns {Promise<{success: boolean, error?: string}>}
   * @example
   *   await window.shiftgearsTest.sendMessage('Show me BMW cars')
   */
  async sendMessage(text) {
    if (!text || text.trim().length === 0) {
      return { success: false, error: 'Message text is required' };
    }

    if (!sendMessageFn) {
      return { success: false, error: 'Chat not initialized. Please wait for widget to load.' };
    }

    try {
      await sendMessageFn(text.trim());
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  /**
   * Get all messages in the current conversation
   * @returns {Array<{id: string, role: string, text: string, timestamp: Date}>}
   * @example
   *   const messages = window.shiftgearsTest.getMessages()
   */
  getMessages() {
    return messagesRef.current.map(msg => ({
      id: msg.id,
      role: msg.role,
      text: msg.text,
      timestamp: msg.timestamp,
      isStreaming: msg.isStreaming,
    }));
  },

  /**
   * Get the last message from a specific role
   * @param {string} role - 'user', 'assistant', or 'system'
   * @returns {Object|null} - Last message or null
   * @example
   *   const lastAssistant = window.shiftgearsTest.getLastMessage('assistant')
   */
  getLastMessage(role = 'assistant') {
    const messages = messagesRef.current.filter(m => m.role === role);
    if (messages.length === 0) return null;
    return messages[messages.length - 1];
  },

  /**
   * Wait for agent response after sending a message
   * @param {number} timeout - Maximum wait time in milliseconds (default: 30000)
   * @returns {Promise<{success: boolean, message?: Object, error?: string}>}
   * @example
   *   await window.shiftgearsTest.sendMessage('Hello')
   *   const response = await window.shiftgearsTest.waitForResponse(15000)
   */
  async waitForResponse(timeout = 30000) {
    const initialCount = messagesRef.current.filter(m => m.role === 'assistant').length;

    const hasNewResponse = () => {
      const currentCount = messagesRef.current.filter(m => m.role === 'assistant').length;
      if (currentCount <= initialCount) return false;

      // Check if the last assistant message is done streaming
      const lastAssistant = messagesRef.current.filter(m => m.role === 'assistant').pop();
      return lastAssistant && !lastAssistant.isStreaming && lastAssistant.text.length > 0;
    };

    const gotResponse = await waitForCondition(hasNewResponse, timeout);

    if (gotResponse) {
      const lastAssistant = messagesRef.current.filter(m => m.role === 'assistant').pop();
      return { success: true, message: lastAssistant };
    }

    return { success: false, error: `Timeout waiting for response (${timeout}ms)` };
  },

  /**
   * Send a message and wait for response in one call
   * @param {string} text - Message to send
   * @param {number} timeout - Wait timeout in milliseconds (default: 30000)
   * @returns {Promise<{success: boolean, response?: string, error?: string}>}
   * @example
   *   const result = await window.shiftgearsTest.sendAndWait('Show me electric cars', 20000)
   *   if (result.success) console.log('Agent said:', result.response)
   */
  async sendAndWait(text, timeout = 30000) {
    const sendResult = await this.sendMessage(text);
    if (!sendResult.success) {
      return sendResult;
    }

    const waitResult = await this.waitForResponse(timeout);
    if (!waitResult.success) {
      return waitResult;
    }

    return {
      success: true,
      response: waitResult.message?.text,
      message: waitResult.message,
    };
  },

  /**
   * Clear all messages
   * Note: This doesn't actually clear - it just returns current state
   * Full clear requires page reload or calling clear in widget
   * @returns {{success: boolean, clearedCount: number}}
   */
  getStatus() {
    return {
      messagesCount: messagesRef.current.length,
      sessionId: sessionIdRef.current,
      wsConnected: browserControlWS.isConnected(),
      widgetReady: sendMessageFn !== null,
    };
  },

  /**
   * Run comprehensive healthcheck
   * @param {Object} options - Healthcheck options
   * @param {boolean} options.testMessage - Send test message (default: false)
   * @param {number} options.timeout - Response timeout (default: 30000)
   * @returns {Promise<{success: boolean, checks: Object, error?: string}>}
   * @example
   *   const health = await window.shiftgearsTest.healthcheck()
   *   const health = await window.shiftgearsTest.healthcheck({ testMessage: true })
   */
  async healthcheck(options = {}) {
    const { testMessage = false, timeout = 30000 } = options;

    const checks = {
      timestamp: new Date().toISOString(),
      widget: {
        ready: sendMessageFn !== null,
        messagesCount: messagesRef.current.length,
      },
      websocket: {
        connected: browserControlWS.isConnected(),
        sessionId: browserControlWS.getSessionId(),
      },
      session: {
        id: sessionIdRef.current,
        stored: getOrCreateSessionId(),
      },
      config: {
        wsUrl: config.BROWSER_CONTROL_WS_URL ? 'configured' : 'missing',
        frontendToken: config.FRONTEND_TOKEN ? 'configured' : 'missing',
        organizationId: config.ORGANIZATION_ID,
        inventoryId: config.INVENTORY_ID,
      },
    };

    // Basic checks
    const basicPassed =
      checks.widget.ready &&
      checks.websocket.connected &&
      checks.session.id &&
      checks.config.wsUrl === 'configured';

    if (!basicPassed) {
      return {
        success: false,
        checks,
        error: 'Basic health checks failed. See checks object for details.',
      };
    }

    // Optional: Test message round-trip
    if (testMessage) {
      console.log('[ShiftGearsTest] Running message round-trip test...');
      const testText = `[HEALTHCHECK] Test message at ${new Date().toISOString()}`;

      const result = await this.sendAndWait(testText, timeout);

      checks.messageTest = {
        sent: testText,
        responseReceived: result.success,
        responseLength: result.response?.length || 0,
        error: result.error,
      };

      if (!result.success) {
        return {
          success: false,
          checks,
          error: `Message test failed: ${result.error}`,
        };
      }
    }

    return {
      success: true,
      checks,
    };
  },

  /**
   * Print help information to console
   */
  help() {
    console.log(`
╔══════════════════════════════════════════════════════════════════╗
║                  ShiftGears Testing API                          ║
╠══════════════════════════════════════════════════════════════════╣
║                                                                  ║
║  QUICK START:                                                    ║
║    const result = await shiftgearsTest.sendAndWait('Hello')      ║
║    console.log(result.response)                                  ║
║                                                                  ║
║  METHODS:                                                        ║
║                                                                  ║
║  sendMessage(text)                                               ║
║    Send a message to the AI agent                                ║
║    Returns: {success, error?}                                    ║
║                                                                  ║
║  getMessages()                                                   ║
║    Get all messages in conversation                              ║
║    Returns: [{id, role, text, timestamp}, ...]                   ║
║                                                                  ║
║  getLastMessage(role='assistant')                                ║
║    Get last message from specified role                          ║
║    Returns: {id, role, text, timestamp} | null                   ║
║                                                                  ║
║  waitForResponse(timeout=30000)                                  ║
║    Wait for next agent response                                  ║
║    Returns: {success, message?, error?}                          ║
║                                                                  ║
║  sendAndWait(text, timeout=30000)                                ║
║    Send message and wait for response                            ║
║    Returns: {success, response?, error?}                         ║
║                                                                  ║
║  healthcheck({testMessage: false, timeout: 30000})               ║
║    Run system healthcheck                                        ║
║    Returns: {success, checks, error?}                            ║
║                                                                  ║
║  getStatus()                                                     ║
║    Get current widget status                                     ║
║    Returns: {messagesCount, sessionId, wsConnected, widgetReady} ║
║                                                                  ║
║  help()                                                          ║
║    Show this help message                                        ║
║                                                                  ║
╚══════════════════════════════════════════════════════════════════╝
`);
  },
};

// Expose on window for browser access
if (typeof window !== 'undefined') {
  window.shiftgearsTest = testingApi;
  console.log('[ShiftGearsTest] Testing API available via window.shiftgearsTest');
  console.log('[ShiftGearsTest] Type window.shiftgearsTest.help() for usage information');
}

export default testingApi;
